/* eslint-disable react/jsx-props-no-spreading */
import {
    useCallback,
    useMemo,
} from 'react'
import {
    GridFilterInputValueProps,
    useGridSelector,
    gridFilterModelSelector,
} from '@mui/x-data-grid-pro'
import {
    Chip,
    Checkbox,
    Autocomplete,
    TextField, ListItemText,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

import {
    getIncludeFiltersFromFilterModel,
    getOtherFilterForBodyFromFilterModel,
} from 'app/PoC/DataGridDemoPages/components/SkyNetDataGrid/utils/FilterModel.utils'

import useLoadFilterArrayOptions from './useLoadFilterArrayOptions'
import useStyles from './ArrayFilterAutocomplete.styles'

type OptionType = {
    value: string | number,
    count: number,
    label: string,
}

export type ArrayFilterAutocompleteInputProps = GridFilterInputValueProps & {
    requestBaseUrl: string,
    dataSourceKey: string,
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

function ArrayFilterAutocompleteInput({
    item,
    applyValue,
    apiRef,
    requestBaseUrl,
    dataSourceKey,
}: Readonly<ArrayFilterAutocompleteInputProps>) {
    const {
        classes,
        cx,
    } = useStyles()

    const filterModel = useGridSelector(apiRef, gridFilterModelSelector)

    const preDefinedFilters = useMemo(() => {
        return {
            includeFilters: getIncludeFiltersFromFilterModel(filterModel, item.field),
            ...getOtherFilterForBodyFromFilterModel(filterModel),
        }
    }, [
        filterModel,
        item,
    ])

    const {
        data,
    } = useLoadFilterArrayOptions<OptionType[]>({
        keyForArchived: dataSourceKey,
        baseUrl: requestBaseUrl,
        filterField: item.field,
        preDefinedFilters,
    })

    const options = useMemo(() => {
        return data || []
    }, [data])

    const value = useMemo(() => {
        return options.filter((option) => {
            return (item.value || []).includes(option.value as string)
        }) || []
    }, [
        item.value,
        options,
    ])

    const sortedOptions = useMemo(() => {
        return [...options].sort((a, b) => {
            const aIsSelected = item.value?.includes(a.value)
            const bIsSelected = item.value?.includes(b.value)

            // put selected options on top
            if (aIsSelected && !bIsSelected) return -1
            if (!aIsSelected && bIsSelected) return 1

            // if both are selected or both are not selected, sort by label
            return a.label.localeCompare(b.label)
        })
    }, [
        item.value,
        options,
    ])

    const onChange = useCallback((_, newValue: OptionType[]) => {
        const result = newValue.map((option) => {
            return option.value
        })

        applyValue({
            ...item, value: result.length > 0 ? result : undefined,
        })
    }, [
        applyValue,
        item,
    ])

    return (
        <div className={classes.wrapper}>
            <Autocomplete
                multiple
                options={sortedOptions}
                value={value}
                size="small"
                onChange={onChange}
                disableCloseOnSelect
                className={classes.autocomplete}
                getOptionLabel={(option) => {
                    return option.label
                }}
                renderOption={(AutoCompleteProps, option, {
                    selected,
                }) => {
                    const {
                        key, ...optionProps
                    } = AutoCompleteProps

                    return (
                        <li
                            key={key}
                            {...optionProps}
                            className={cx(classes.option, optionProps.className)}
                        >
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{
                                    marginRight: 8,
                                }}
                                checked={selected}
                            />
                            <ListItemText
                                id={key}
                                primary={option.label}
                                className={classes.optionLabel}
                            />
                            <Chip
                                size="small"
                                label={option.count}
                            />
                        </li>
                    )
                }}
                renderInput={(params) => {
                    return (
                        <TextField
                            variant="standard"
                            placeholder={value.length === 0 ? 'Select' : undefined}
                            {...params}
                        />
                    )
                }}
                componentsProps={{
                    popper: {
                        className: classes.dropdown,
                    },
                }}
            />
        </div>
    )
}

export default ArrayFilterAutocompleteInput
